export const fmtMoney = (n, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(n || 0));

export const fmtCents = (c) => fmtMoney((c || 0) / 100);

export const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const fmtTime = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

export const fmtDateTime = (d) => `${fmtDate(d)} · ${fmtTime(d)}`;

// Zone-aware rendering (Part 8/9): booking start/end are listing-local civil
// times — render them in the LISTING's IANA zone ("Sep 24, 2026, 8:00 PM EDT"),
// never reinterpreted in the browser's own zone. Legacy records without a tz
// keep the old browser-local fallback.
export const fmtDateTimeZone = (d, tz) => {
  if (!d) return "—";
  if (!tz) return fmtDateTime(d);
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz, month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit", timeZoneName: "short",
    }).format(new Date(d));
  } catch { return fmtDateTime(d); }
};

export const hoursBetween = (a, b) =>
  Math.max(0, (new Date(b).getTime() - new Date(a).getTime()) / 3600000);

// Canonical property-timezone label: "America/Toronto (EDT)" — never a device tz.
export const tzLabel = (tz) => {
  if (!tz) return "";
  try {
    const abbr = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
      .formatToParts(new Date()).find((p) => p.type === "timeZoneName")?.value || "";
    return `${tz}${abbr ? ` (${abbr})` : ""}`;
  } catch { return tz; }
};

// Convert a UTC instant to listing-local wall-clock parts {d: "YYYY-MM-DD", t: "HH:MM"}.
// Used wherever an absolute instant must be presented as property-local time —
// never let the device timezone silently shift a booking instant.
export const instantToZoneParts = (iso, tz) => {
  if (!iso || !tz) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(new Date(iso));
    const g = (t) => parts.find((p) => p.type === t)?.value;
    const hh = g("hour") === "24" ? "00" : g("hour");
    return { d: `${g("year")}-${g("month")}-${g("day")}`, t: `${hh}:${g("minute")}` };
  } catch { return null; }
};

export const fmtDuration = (a, b) => {
  const h = hoursBetween(a, b);
  if (h < 24) return `${h % 1 === 0 ? h : h.toFixed(1)} hr${h === 1 ? "" : "s"}`;
  const d = h / 24;
  return `${d % 1 === 0 ? d : d.toFixed(1)} day${d === 1 ? "" : "s"}`;
};

export const setPageTitle = (title) => {
  document.title = title ? `${title} — MyRedPark` : "MyRedPark — Your Spot. Your Way.";
};

export const SPACE_TYPE_ICONS = {
  Underground: "🏢",
  Covered: "🏗️",
  Open: "🅿️",
  Rooftop: "🏙️",
  Private: "🔒",
};
